import { FC, memo, ReactElement, useContext, useMemo } from 'react'
import { useTranslate } from 'react-polyglot'
import { NavigationRailState } from '@getgo/chameleon-core/next'
import {
  BankCardOutlinedIcon,
  HomeFilledIcon,
  HomeOutlinedIcon,
  InboxFilledIcon,
  LandlineFilledIcon,
  OfficeBuildingFilledIcon,
  OfficeBuildingOutlinedIcon,
  PbxFilledIcon,
  PersonFilledIcon,
  PersonOutlinedIcon,
  SettingsFilledIcon,
  SettingsOutlinedIcon,
  VrFaceMaleFilledIcon,
  VrFaceMaleOutlinedIcon,
} from '@getgo/chameleon-icons/react'
import { NavigationRail, NavigationRailItem } from '@getgo/chameleon-web-react-wrapper'
import { makeStyles } from '@material-ui/core'
import { useLocalStorage } from '@rehooks/local-storage'
import {
  BILLING_ROUTE,
  DEVICES_ROUTE,
  DIGITAL_WORKFORCE_ROUTE,
  GTC_BILLING_ROUTE,
  PEOPLE_ROUTE,
  PHONES_ROUTE,
  PLACES_ROUTE,
  SETTINGS_ROUTE,
} from '@ucc/unified-admin-common'
import mixpanel from 'mixpanel-browser'

import config from 'config'
import useBillingFeatures from 'hooks/useBillingFeatures'
import useGtcBillingFeatures from 'hooks/useGtcBillingFeatures'
import { SidebarLink } from 'types/SidebarLink'
import { AppLinks, mixpanelEventNames } from 'utils'
import { calculateBannerHeight } from 'utils/bannerMargin'
import { HOME_KEY } from 'utils/constants'
import useGtcBillingAccess from '../hooks/useGtcBillingAccess'
import { UnleashContext } from './providers/UnleashProvider'

const useStyles = makeStyles(() => ({
  root: (props: { bannerMargin: boolean }) => ({
    boxSizing: 'border-box',
    height: `calc(100vh - ${calculateBannerHeight(props)}px)`,
  }),
  railItem: {
    boxSizing: 'border-box',
  },
}))

interface SmallSidebarProps {
  onSelect: (to: string, links: SidebarLink[]) => void
  selectedTab: string
  bannerMargin: boolean
}

const SmallSidebar: FC<SmallSidebarProps> = ({ onSelect, bannerMargin, selectedTab }) => {
  const t = useTranslate()
  const links = AppLinks()
  const classes = useStyles({ bannerMargin })
  const { isBillingAdmin, systemUserHasBillingAccess, partnerHasChildBillingAccess } = useBillingFeatures()
  const { inboxSettingsIntegrationEnabled } = useContext(UnleashContext)

  const TOGGLE_STATE_COLLAPSED = 'collapsed'
  const TOGGLE_STATE_EXPANDED = 'expanded'
  const [sidebarToggleState, setSidebarToggleState] = useLocalStorage<NavigationRailState>(
    config.SIDEBAR_TOGGLE_STATE,
    'collapsed',
  )

  const { isInternalUser } = useGtcBillingFeatures()
  const { isGtcBillingAccount } = useGtcBillingAccess()
  const gtcBillingPrimary = useMemo(() => {
    return isInternalUser ? t('Jive Billing') : t('Billing')
  }, [isInternalUser, t])

  const isBillingAccessible = isBillingAdmin || systemUserHasBillingAccess || partnerHasChildBillingAccess

  interface SmallSidebarLink {
    to: string
    primary: string
    activeIcon: ReactElement
    inactiveIcon: ReactElement
  }

  const smallSidebarLinks: SmallSidebarLink[] = [
    {
      to: HOME_KEY,
      primary: t('Home'),
      activeIcon: <HomeFilledIcon size="24px" />,
      inactiveIcon: <HomeOutlinedIcon size="24px" />,
    },
    {
      to: PEOPLE_ROUTE,
      primary: t('People'),
      activeIcon: <PersonFilledIcon size="24px" />,
      inactiveIcon: <PersonOutlinedIcon size="24px" />,
    },
    ...(inboxSettingsIntegrationEnabled
      ? [
          {
            to: 'inbox',
            primary: t('Inbox'),
            activeIcon: <InboxFilledIcon size="24px" />,
            inactiveIcon: <InboxFilledIcon size="24px" />,
          },
        ]
      : []),
    {
      to: DIGITAL_WORKFORCE_ROUTE,
      primary: t('Digital Workforce'),
      activeIcon: <VrFaceMaleFilledIcon size="24px" />,
      inactiveIcon: <VrFaceMaleOutlinedIcon size="24px" />,
    },
    {
      to: DEVICES_ROUTE,
      primary: t('Devices'),
      activeIcon: <LandlineFilledIcon size="24px" />,
      inactiveIcon: <LandlineFilledIcon size="24px" />,
    },
    {
      to: PLACES_ROUTE,
      primary: t('Locations'),
      activeIcon: <OfficeBuildingFilledIcon size="24px" />,
      inactiveIcon: <OfficeBuildingOutlinedIcon size="24px" />,
    },
    {
      to: PHONES_ROUTE,
      primary: t('Phone system'),
      activeIcon: <PbxFilledIcon size="24px" />,
      inactiveIcon: <PbxFilledIcon size="24px" />,
    },

    ...(isBillingAccessible
      ? [
          {
            to: BILLING_ROUTE,
            primary: t('Billing'),
            activeIcon: <BankCardOutlinedIcon size="24px" />,
            inactiveIcon: <BankCardOutlinedIcon size="24px" />,
          },
        ]
      : []),

    ...(isGtcBillingAccount
      ? [
          {
            to: GTC_BILLING_ROUTE,
            primary: gtcBillingPrimary,
            activeIcon: <BankCardOutlinedIcon size="24px" />,
            inactiveIcon: <BankCardOutlinedIcon size="24px" />,
          },
        ]
      : []),
    {
      to: SETTINGS_ROUTE,
      primary: t('Settings'),
      activeIcon: <SettingsFilledIcon size="24px" />,
      inactiveIcon: <SettingsOutlinedIcon size="24px" />,
    },
  ]

  const sendLinkMixPanelEvent = (to: string) => {
    if (to === PLACES_ROUTE) {
      mixpanel.track(mixpanelEventNames.LOCATIONS, {
        Action: 'Navigated to Locations page',
      })
    }
  }

  const handleLinkClick = (to: string) => {
    sendLinkMixPanelEvent(to)
    onSelect(to, links[to])
  }

  const handleToggle = () => {
    setSidebarToggleState(sidebarToggleState == TOGGLE_STATE_COLLAPSED ? TOGGLE_STATE_EXPANDED : TOGGLE_STATE_COLLAPSED)
  }
  return (
    <NavigationRail
      state={sidebarToggleState}
      className={classes.root}
      onToggle={handleToggle}
      collapseLabel={t('Collapse')}
      expandLabel={t('Expand')}
    >
      {smallSidebarLinks.map(
        ({ to, activeIcon, primary, inactiveIcon }) =>
          links[to] &&
          links[to].some((link) => link.key && link.key !== 'skeleton') && ( // only show the button if there's at least one link inside
            <NavigationRailItem
              key={to}
              className={classes.railItem}
              selected={selectedTab === to}
              icon={selectedTab === to ? activeIcon : inactiveIcon}
              onClick={() => handleLinkClick(to)}
            >
              {primary}
            </NavigationRailItem>
          ),
      )}
    </NavigationRail>
  )
}

export default memo(SmallSidebar)
